import React, { useState, useMemo } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const MortgageCalculatorPage = () => {
  const currentDate = new Date();
  const [homePrice, setHomePrice] = useState('400000');
  const [downPayment, setDownPayment] = useState('20');
  const [downPaymentType, setDownPaymentType] = useState('percent');
  const [loanTerm, setLoanTerm] = useState('30');
  const [interestRate, setInterestRate] = useState('6.22');
  const [startMonth, setStartMonth] = useState(String(currentDate.getMonth()));
  const [startYear, setStartYear] = useState(String(currentDate.getFullYear()));
  
  // Taxes & Costs
  const [showTaxesCosts, setShowTaxesCosts] = useState(true);
  const [propertyTax, setPropertyTax] = useState('4800');
  const [propertyTaxType, setPropertyTaxType] = useState('dollar');
  const [homeInsurance, setHomeInsurance] = useState('1500');
  const [homeInsuranceType, setHomeInsuranceType] = useState('dollar');
  const [pmi, setPmi] = useState('');
  const [pmiType, setPmiType] = useState('percent');
  const [hoaFee, setHoaFee] = useState('');
  const [hoaFeeType, setHoaFeeType] = useState('dollar');
  const [otherCosts, setOtherCosts] = useState('');
  const [otherCostsType, setOtherCostsType] = useState('dollar');

  const [result, setResult] = useState(null);
  const [scheduleView, setScheduleView] = useState('yearly'); // 'monthly' or 'yearly'
  const [showSchedule, setShowSchedule] = useState(false);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const years = Array.from({ length: 10 }, (_, i) => currentDate.getFullYear() + i);

  const calculateMortgage = () => {
    const price = parseFloat(homePrice) || 0;
    let downPaymentAmount = downPaymentType === 'percent' 
      ? price * (parseFloat(downPayment) / 100)
      : parseFloat(downPayment) || 0;
    
    const principal = price - downPaymentAmount;
    const annualRate = parseFloat(interestRate) / 100;
    const monthlyRate = annualRate / 12;
    const totalMonths = parseFloat(loanTerm) * 12;

    // Calculate annual costs
    const calcAnnualCost = (value, type, basePrice) => {
      const val = parseFloat(value) || 0;
      return type === 'percent' ? (basePrice * val / 100) : val;
    };

    const annualPropertyTax = calcAnnualCost(propertyTax, propertyTaxType, price);
    const annualInsurance = calcAnnualCost(homeInsurance, homeInsuranceType, price);
    const annualPmi = calcAnnualCost(pmi, pmiType, principal);
    const annualHoa = calcAnnualCost(hoaFee, hoaFeeType, price) * 12; // HOA is monthly input
    const annualOther = calcAnnualCost(otherCosts, otherCostsType, price);

    const monthlyTax = showTaxesCosts ? annualPropertyTax / 12 : 0;
    const monthlyInsurance = showTaxesCosts ? annualInsurance / 12 : 0;
    const monthlyPmi = showTaxesCosts ? annualPmi / 12 : 0;
    const monthlyHoa = showTaxesCosts ? (parseFloat(hoaFee) || 0) : 0;
    const monthlyOther = showTaxesCosts ? annualOther / 12 : 0;

    if (principal > 0 && monthlyRate > 0 && totalMonths > 0) {
      // Monthly P&I payment
      const monthlyPI = (principal * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / 
                        (Math.pow(1 + monthlyRate, totalMonths) - 1);
      
      const totalMonthlyPayment = monthlyPI + monthlyTax + monthlyInsurance + monthlyPmi + monthlyHoa + monthlyOther;
      const totalMortgagePayments = monthlyPI * totalMonths;
      const totalInterest = totalMortgagePayments - principal;

      // Calculate payoff date
      const startM = parseInt(startMonth);
      const startY = parseInt(startYear);
      const payoffMonth = (startM + totalMonths - 1) % 12;
      const payoffYear = startY + Math.floor((startM + totalMonths - 1) / 12);

      // Generate amortization schedule
      const schedule = [];
      let balance = principal;
      let scheduleMonth = startM;
      let scheduleYear = startY;

      for (let i = 1; i <= totalMonths; i++) {
        const interestPayment = balance * monthlyRate;
        const principalPayment = monthlyPI - interestPayment;
        balance = Math.max(0, balance - principalPayment);

        schedule.push({
          month: i,
          date: `${months[scheduleMonth]}/${scheduleYear}`,
          interest: interestPayment,
          principal: principalPayment,
          balance: balance,
          year: Math.ceil(i / 12)
        });

        scheduleMonth++;
        if (scheduleMonth > 11) {
          scheduleMonth = 0;
          scheduleYear++;
        }
      }

      // Calculate yearly summaries
      const yearlySchedule = [];
      for (let year = 1; year <= parseInt(loanTerm); year++) {
        const yearPayments = schedule.filter(s => s.year === year);
        if (yearPayments.length > 0) {
          const totalYearInterest = yearPayments.reduce((sum, p) => sum + p.interest, 0);
          const totalYearPrincipal = yearPayments.reduce((sum, p) => sum + p.principal, 0);
          const startDate = yearPayments[0].date;
          const endDate = yearPayments[yearPayments.length - 1].date;
          yearlySchedule.push({
            year,
            dateRange: `${startDate}-${endDate}`,
            interest: totalYearInterest,
            principal: totalYearPrincipal,
            balance: yearPayments[yearPayments.length - 1].balance
          });
        }
      }

      setResult({
        monthlyPayment: totalMonthlyPayment,
        monthlyPI: monthlyPI,
        monthlyTax,
        monthlyInsurance,
        monthlyPmi,
        monthlyHoa,
        monthlyOther,
        totalMortgagePayments,
        totalInterest,
        totalOutOfPocket: totalMonthlyPayment * totalMonths,
        housePrice: price,
        loanAmount: principal,
        downPaymentAmount,
        payoffDate: `${months[payoffMonth]}. ${payoffYear}`,
        totalMonths,
        schedule,
        yearlySchedule,
        // For pie chart percentages
        piPercent: (monthlyPI / totalMonthlyPayment * 100).toFixed(0),
        taxPercent: (monthlyTax / totalMonthlyPayment * 100).toFixed(0),
        insurancePercent: (monthlyInsurance / totalMonthlyPayment * 100).toFixed(0),
        otherPercent: ((monthlyPmi + monthlyHoa + monthlyOther) / totalMonthlyPayment * 100).toFixed(0)
      });
    }
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    }).format(num);
  };

  const formatCurrencyShort = (num) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    }).format(num);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/financial-calculators" className="text-cyan-400 hover:text-cyan-300">Financial Calculators</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Mortgage Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-6">Mortgage Calculator</h1>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            {/* Main Input Fields */}
            <div className="space-y-4">
              {/* Home Price */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-32">Home Price</label>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">$</span>
                  <Input
                    type="number"
                    value={homePrice}
                    onChange={(e) => setHomePrice(e.target.value)}
                    className="w-36 bg-[#0d0d0d] border-[#2a2a2a] text-white"
                  />
                </div>
              </div>

              {/* Down Payment */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-32">Down Payment</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={downPayment}
                    onChange={(e) => setDownPayment(e.target.value)}
                    className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white"
                  />
                  <div className="flex border border-[#2a2a2a] rounded overflow-hidden">
                    <button
                      onClick={() => setDownPaymentType('percent')}
                      className={`px-3 py-1.5 text-sm ${downPaymentType === 'percent' ? 'bg-cyan-600 text-white' : 'bg-[#0d0d0d] text-gray-400'}`}
                    >%</button>
                    <button
                      onClick={() => setDownPaymentType('dollar')}
                      className={`px-3 py-1.5 text-sm ${downPaymentType === 'dollar' ? 'bg-cyan-600 text-white' : 'bg-[#0d0d0d] text-gray-400'}`}
                    >$</button>
                  </div>
                </div>
              </div>

              {/* Loan Term */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-32">Loan Term</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={loanTerm}
                    onChange={(e) => setLoanTerm(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white"
                  />
                  <span className="text-gray-400">years</span>
                </div>
              </div>

              {/* Interest Rate */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-32">Interest Rate</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                    className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white"
                  />
                  <span className="text-gray-400">%</span>
                </div>
              </div>

              {/* Start Date */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-32">Start Date</label>
                <div className="flex items-center gap-2">
                  <select
                    value={startMonth}
                    onChange={(e) => setStartMonth(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white"
                  >
                    {months.map((m, i) => (
                      <option key={m} value={i}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={startYear}
                    onChange={(e) => setStartYear(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white"
                  >
                    {years.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Taxes & Costs Section */}
            <div className="mt-6 pt-6 border-t border-[#2a2a2a]">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showTaxesCosts}
                  onChange={(e) => setShowTaxesCosts(e.target.checked)}
                  className="w-4 h-4 accent-cyan-500"
                />
                <span className="text-white font-semibold">Include Taxes & Costs Below</span>
              </label>

              {showTaxesCosts && (
                <div className="mt-4 space-y-3 pl-7">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Property Taxes */}
                    <div className="flex items-center gap-2">
                      <label className="text-gray-400 text-sm w-28">Property Taxes</label>
                      <Input
                        type="number"
                        value={propertyTax}
                        onChange={(e) => setPropertyTax(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white text-sm"
                      />
                      <div className="flex border border-[#2a2a2a] rounded overflow-hidden">
                        <button onClick={() => setPropertyTaxType('percent')} className={`px-2 py-1 text-xs ${propertyTaxType === 'percent' ? 'bg-cyan-600 text-white' : 'bg-[#0d0d0d] text-gray-400'}`}>%</button>
                        <button onClick={() => setPropertyTaxType('dollar')} className={`px-2 py-1 text-xs ${propertyTaxType === 'dollar' ? 'bg-cyan-600 text-white' : 'bg-[#0d0d0d] text-gray-400'}`}>$</button>
                      </div>
                    </div>

                    {/* Home Insurance */}
                    <div className="flex items-center gap-2">
                      <label className="text-gray-400 text-sm w-28">Home Insurance</label>
                      <Input
                        type="number"
                        value={homeInsurance}
                        onChange={(e) => setHomeInsurance(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white text-sm"
                      />
                      <div className="flex border border-[#2a2a2a] rounded overflow-hidden">
                        <button onClick={() => setHomeInsuranceType('percent')} className={`px-2 py-1 text-xs ${homeInsuranceType === 'percent' ? 'bg-cyan-600 text-white' : 'bg-[#0d0d0d] text-gray-400'}`}>%</button>
                        <button onClick={() => setHomeInsuranceType('dollar')} className={`px-2 py-1 text-xs ${homeInsuranceType === 'dollar' ? 'bg-cyan-600 text-white' : 'bg-[#0d0d0d] text-gray-400'}`}>$</button>
                      </div>
                    </div>

                    {/* PMI */}
                    <div className="flex items-center gap-2">
                      <label className="text-gray-400 text-sm w-28">PMI Insurance</label>
                      <Input
                        type="number"
                        value={pmi}
                        onChange={(e) => setPmi(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white text-sm"
                        placeholder="0"
                      />
                      <div className="flex border border-[#2a2a2a] rounded overflow-hidden">
                        <button onClick={() => setPmiType('percent')} className={`px-2 py-1 text-xs ${pmiType === 'percent' ? 'bg-cyan-600 text-white' : 'bg-[#0d0d0d] text-gray-400'}`}>%</button>
                        <button onClick={() => setPmiType('dollar')} className={`px-2 py-1 text-xs ${pmiType === 'dollar' ? 'bg-cyan-600 text-white' : 'bg-[#0d0d0d] text-gray-400'}`}>$</button>
                      </div>
                    </div>

                    {/* HOA Fee */}
                    <div className="flex items-center gap-2">
                      <label className="text-gray-400 text-sm w-28">HOA Fee</label>
                      <Input
                        type="number"
                        value={hoaFee}
                        onChange={(e) => setHoaFee(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white text-sm"
                        placeholder="0"
                      />
                      <span className="text-gray-500 text-xs">/mo</span>
                    </div>

                    {/* Other Costs */}
                    <div className="flex items-center gap-2">
                      <label className="text-gray-400 text-sm w-28">Other Costs</label>
                      <Input
                        type="number"
                        value={otherCosts}
                        onChange={(e) => setOtherCosts(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white text-sm"
                        placeholder="0"
                      />
                      <div className="flex border border-[#2a2a2a] rounded overflow-hidden">
                        <button onClick={() => setOtherCostsType('percent')} className={`px-2 py-1 text-xs ${otherCostsType === 'percent' ? 'bg-cyan-600 text-white' : 'bg-[#0d0d0d] text-gray-400'}`}>%</button>
                        <button onClick={() => setOtherCostsType('dollar')} className={`px-2 py-1 text-xs ${otherCostsType === 'dollar' ? 'bg-cyan-600 text-white' : 'bg-[#0d0d0d] text-gray-400'}`}>$</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Calculate Button */}
            <div className="mt-6 flex gap-3">
              <Button onClick={calculateMortgage} className="bg-cyan-600 hover:bg-cyan-500 text-white px-8">
                Calculate
              </Button>
            </div>

            {/* Results */}
            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                {/* Monthly Payment Header */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <h3 className="text-xl font-bold text-white">
                    Monthly Pay: <span className="text-green-400">{formatCurrency(result.monthlyPayment)}</span>
                  </h3>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Payment Breakdown Table */}
                  <div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#2a2a2a]">
                          <th className="text-left py-2 text-gray-400 font-medium"></th>
                          <th className="text-right py-2 text-gray-400 font-medium">Monthly</th>
                          <th className="text-right py-2 text-gray-400 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-[#2a2a2a]">
                          <td className="py-2 text-white font-semibold">Mortgage Payment</td>
                          <td className="py-2 text-right text-white font-semibold">{formatCurrency(result.monthlyPI)}</td>
                          <td className="py-2 text-right text-white font-semibold">{formatCurrency(result.totalMortgagePayments)}</td>
                        </tr>
                        {result.monthlyTax > 0 && (
                          <tr className="border-b border-[#2a2a2a]">
                            <td className="py-2 text-gray-300">Property Tax</td>
                            <td className="py-2 text-right text-gray-300">{formatCurrency(result.monthlyTax)}</td>
                            <td className="py-2 text-right text-gray-300">{formatCurrency(result.monthlyTax * result.totalMonths)}</td>
                          </tr>
                        )}
                        {result.monthlyInsurance > 0 && (
                          <tr className="border-b border-[#2a2a2a]">
                            <td className="py-2 text-gray-300">Home Insurance</td>
                            <td className="py-2 text-right text-gray-300">{formatCurrency(result.monthlyInsurance)}</td>
                            <td className="py-2 text-right text-gray-300">{formatCurrency(result.monthlyInsurance * result.totalMonths)}</td>
                          </tr>
                        )}
                        {result.monthlyPmi > 0 && (
                          <tr className="border-b border-[#2a2a2a]">
                            <td className="py-2 text-gray-300">PMI</td>
                            <td className="py-2 text-right text-gray-300">{formatCurrency(result.monthlyPmi)}</td>
                            <td className="py-2 text-right text-gray-300">{formatCurrency(result.monthlyPmi * result.totalMonths)}</td>
                          </tr>
                        )}
                        {result.monthlyHoa > 0 && (
                          <tr className="border-b border-[#2a2a2a]">
                            <td className="py-2 text-gray-300">HOA Fee</td>
                            <td className="py-2 text-right text-gray-300">{formatCurrency(result.monthlyHoa)}</td>
                            <td className="py-2 text-right text-gray-300">{formatCurrency(result.monthlyHoa * result.totalMonths)}</td>
                          </tr>
                        )}
                        {result.monthlyOther > 0 && (
                          <tr className="border-b border-[#2a2a2a]">
                            <td className="py-2 text-gray-300">Other Costs</td>
                            <td className="py-2 text-right text-gray-300">{formatCurrency(result.monthlyOther)}</td>
                            <td className="py-2 text-right text-gray-300">{formatCurrency(result.monthlyOther * result.totalMonths)}</td>
                          </tr>
                        )}
                        <tr className="bg-[#1a1a1a]">
                          <td className="py-2 text-cyan-400 font-semibold">Total Out-of-Pocket</td>
                          <td className="py-2 text-right text-cyan-400 font-semibold">{formatCurrency(result.monthlyPayment)}</td>
                          <td className="py-2 text-right text-cyan-400 font-semibold">{formatCurrency(result.totalOutOfPocket)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Pie Chart Visualization */}
                  <div className="flex flex-col items-center justify-center">
                    <div className="relative w-40 h-40">
                      <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                        {/* Principal & Interest */}
                        <circle
                          cx="50" cy="50" r="40"
                          fill="transparent"
                          stroke="#06b6d4"
                          strokeWidth="20"
                          strokeDasharray={`${result.piPercent * 2.51} 251`}
                        />
                        {/* Property Tax */}
                        <circle
                          cx="50" cy="50" r="40"
                          fill="transparent"
                          stroke="#8b5cf6"
                          strokeWidth="20"
                          strokeDasharray={`${result.taxPercent * 2.51} 251`}
                          strokeDashoffset={`-${result.piPercent * 2.51}`}
                        />
                        {/* Insurance */}
                        <circle
                          cx="50" cy="50" r="40"
                          fill="transparent"
                          stroke="#f59e0b"
                          strokeWidth="20"
                          strokeDasharray={`${result.insurancePercent * 2.51} 251`}
                          strokeDashoffset={`-${(parseFloat(result.piPercent) + parseFloat(result.taxPercent)) * 2.51}`}
                        />
                        {/* Other */}
                        <circle
                          cx="50" cy="50" r="40"
                          fill="transparent"
                          stroke="#10b981"
                          strokeWidth="20"
                          strokeDasharray={`${result.otherPercent * 2.51} 251`}
                          strokeDashoffset={`-${(parseFloat(result.piPercent) + parseFloat(result.taxPercent) + parseFloat(result.insurancePercent)) * 2.51}`}
                        />
                      </svg>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-cyan-500 rounded"></div>
                        <span className="text-gray-400">{result.piPercent}% Principal & Interest</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-purple-500 rounded"></div>
                        <span className="text-gray-400">{result.taxPercent}% Property Taxes</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-amber-500 rounded"></div>
                        <span className="text-gray-400">{result.insurancePercent}% Home Insurance</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-emerald-500 rounded"></div>
                        <span className="text-gray-400">{result.otherPercent}% Other</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="mt-6 pt-4 border-t border-[#2a2a2a] grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">House Price</span>
                    <p className="text-white font-semibold">{formatCurrency(result.housePrice)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Loan Amount</span>
                    <p className="text-white font-semibold">{formatCurrency(result.loanAmount)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Down Payment</span>
                    <p className="text-white font-semibold">{formatCurrency(result.downPaymentAmount)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Total of {result.totalMonths} Payments</span>
                    <p className="text-white font-semibold">{formatCurrency(result.totalMortgagePayments)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Interest</span>
                    <p className="text-white font-semibold">{formatCurrency(result.totalInterest)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Mortgage Payoff Date</span>
                    <p className="text-white font-semibold">{result.payoffDate}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Amortization Schedule */}
          {result && (
            <div className="mt-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Amortization Schedule</h2>
                <button
                  onClick={() => setShowSchedule(!showSchedule)}
                  className="text-cyan-400 hover:text-cyan-300 text-sm"
                >
                  {showSchedule ? 'Hide Schedule' : 'Show Schedule'}
                </button>
              </div>

              {showSchedule && (
                <>
                  <div className="flex gap-4 mb-4">
                    <button
                      onClick={() => setScheduleView('yearly')}
                      className={`px-4 py-2 rounded text-sm ${scheduleView === 'yearly' ? 'bg-cyan-600 text-white' : 'bg-[#2a2a2a] text-gray-400'}`}
                    >
                      Annual Schedule
                    </button>
                    <button
                      onClick={() => setScheduleView('monthly')}
                      className={`px-4 py-2 rounded text-sm ${scheduleView === 'monthly' ? 'bg-cyan-600 text-white' : 'bg-[#2a2a2a] text-gray-400'}`}
                    >
                      Monthly Schedule
                    </button>
                  </div>

                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-[#1a1a1a]">
                        <tr className="border-b border-[#2a2a2a]">
                          <th className="text-left py-2 px-2 text-gray-400">{scheduleView === 'yearly' ? 'Year' : 'Month'}</th>
                          <th className="text-left py-2 px-2 text-gray-400">Date</th>
                          <th className="text-right py-2 px-2 text-gray-400">Interest</th>
                          <th className="text-right py-2 px-2 text-gray-400">Principal</th>
                          <th className="text-right py-2 px-2 text-gray-400">Ending Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scheduleView === 'yearly' ? (
                          result.yearlySchedule.map((row) => (
                            <tr key={row.year} className="border-b border-[#2a2a2a] hover:bg-[#2a2a2a]/50">
                              <td className="py-2 px-2 text-white">{row.year}</td>
                              <td className="py-2 px-2 text-gray-400">{row.dateRange}</td>
                              <td className="py-2 px-2 text-right text-gray-300">{formatCurrencyShort(row.interest)}</td>
                              <td className="py-2 px-2 text-right text-gray-300">{formatCurrencyShort(row.principal)}</td>
                              <td className="py-2 px-2 text-right text-white font-medium">{formatCurrencyShort(row.balance)}</td>
                            </tr>
                          ))
                        ) : (
                          result.schedule.slice(0, 60).map((row) => (
                            <React.Fragment key={row.month}>
                              <tr className="border-b border-[#2a2a2a] hover:bg-[#2a2a2a]/50">
                                <td className="py-2 px-2 text-white">{row.month}</td>
                                <td className="py-2 px-2 text-gray-400">{row.date}</td>
                                <td className="py-2 px-2 text-right text-gray-300">{formatCurrencyShort(row.interest)}</td>
                                <td className="py-2 px-2 text-right text-gray-300">{formatCurrencyShort(row.principal)}</td>
                                <td className="py-2 px-2 text-right text-white font-medium">{formatCurrencyShort(row.balance)}</td>
                              </tr>
                              {row.month % 12 === 0 && (
                                <tr className="bg-[#0d0d0d]">
                                  <td colSpan="5" className="py-1 px-2 text-center text-gray-500 text-xs">
                                    End of year {row.year}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))
                        )}
                      </tbody>
                    </table>
                    {scheduleView === 'monthly' && result.schedule.length > 60 && (
                      <p className="text-center text-gray-500 text-sm py-4">
                        Showing first 60 months. Full schedule contains {result.totalMonths} payments.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Related Calculators */}
          <div className="mt-6 flex flex-wrap gap-2 text-sm">
            <span className="text-gray-500">Related:</span>
            <Link to="/loan-calculator" className="text-cyan-400 hover:text-cyan-300">Loan Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/auto-loan-calculator" className="text-cyan-400 hover:text-cyan-300">Auto Loan Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/interest-calculator" className="text-cyan-400 hover:text-cyan-300">Interest Calculator</Link>
          </div>

          {/* Information Sections */}
          <div className="mt-10 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Mortgages</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                A mortgage is a loan secured by property, usually real estate property. Lenders define it as the money 
                borrowed to pay for real estate. In essence, the lender helps the buyer pay the seller of a house, and 
                the buyer agrees to repay the money borrowed over a period of time, usually 15 or 30 years in the U.S.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Each month, a payment is made from buyer to lender. A portion of the monthly payment is called the principal, 
                which is the original amount borrowed. The other portion is the interest, which is the cost paid to the 
                lender for using the money.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Mortgage Calculator Components</h2>
              <div className="space-y-4">
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Loan Amount</h3>
                  <p className="text-gray-400 text-sm">
                    The amount borrowed from a lender or bank. In a mortgage, this amounts to the purchase price minus any down payment.
                  </p>
                </div>
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Down Payment</h3>
                  <p className="text-gray-400 text-sm">
                    The upfront payment of the purchase, usually a percentage of the total price. Typically, mortgage lenders 
                    want the borrower to put 20% or more as a down payment.
                  </p>
                </div>
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Loan Term</h3>
                  <p className="text-gray-400 text-sm">
                    The amount of time over which the loan must be repaid in full. Most fixed-rate mortgages are for 15, 20, or 30-year terms.
                  </p>
                </div>
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Interest Rate</h3>
                  <p className="text-gray-400 text-sm">
                    The percentage of the loan charged as a cost of borrowing. Mortgage interest rates are normally expressed in 
                    Annual Percentage Rate (APR).
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Costs Associated with Mortgages</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Property Taxes</h3>
                  <p className="text-gray-400 text-sm">
                    A tax that property owners pay to governing authorities. On average, Americans pay about 1.1% of their 
                    property's value as property tax each year.
                  </p>
                </div>
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Home Insurance</h3>
                  <p className="text-gray-400 text-sm">
                    An insurance policy that protects the owner from accidents that may happen to their real estate properties.
                  </p>
                </div>
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">PMI (Private Mortgage Insurance)</h3>
                  <p className="text-gray-400 text-sm">
                    Protects the mortgage lender if the borrower is unable to repay the loan. Required if down payment is less than 20%.
                  </p>
                </div>
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">HOA Fees</h3>
                  <p className="text-gray-400 text-sm">
                    A fee imposed on the property owner by a homeowner's association (HOA) for maintaining and improving the property.
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* More Financial Calculators */}
          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'Mortgage', path: '/mortgage-calculator' },
                { name: 'Loan', path: '/loan-calculator' },
                { name: 'Auto Loan', path: '/auto-loan-calculator' },
                { name: 'Interest', path: '/interest-calculator' },
                { name: 'Payment', path: '/payment-calculator' },
                { name: 'Retirement', path: '/retirement-calculator' },
                { name: 'Amortization', path: '/amortization-calculator' },
                { name: 'Investment', path: '/investment-calculator' },
                { name: 'Compound Interest', path: '/compound-interest-calculator' },
              ].map((calc) => (
                <Link 
                  key={calc.path}
                  to={calc.path} 
                  className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 hover:border-cyan-500/50 text-sm transition-colors"
                >
                  {calc.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MortgageCalculatorPage;
